/**
 * Image decryption keys never leave over MCP (audit card MCP 1, 04.10.2026).
 *
 * Document images, rich comments and task descriptions store each encrypted
 * image as `<img src=… data-enc-key="…" data-enc="aes-gcm-256">` (saved HTML)
 * or as a `doc-image` block node with `k` (the editor's block snapshot). The
 * blobs sit in PUBLIC-READ storage, so the URL plus the key decrypts the file
 * for anyone holding the agent's transcript — teammates' images included.
 *
 * Three layers, so a new read path cannot forget:
 *  1. the renderers (`mcp/markdown.js` `renderBody`) turn an image into a
 *     readable "🖼 Image: name" line, and the html format drops the keys;
 *  2. `scrubMediaKeysDeep` runs on EVERY result `runHandler` returns
 *     (handlers/index.js) — the single place all JS tool output passes;
 *  3. Rust `redact::scrub_value` strips the attribute again at the socket,
 *     which also covers the Rust-native tools.
 *
 * The key the (Rust-native) media upload tool hands the agent for ITS OWN upload is a
 * plain `key` field, not one of these shapes, and is deliberately untouched.
 */

/** `data-enc-key` / `data-enc` with any (or no) value, in any quoting. */
const KEY_ATTR_RE = /\s*\bdata-enc(?:-key)?(?=[\s=/>]|$)(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+))?/gi;

/** Cheap pre-check, so the deep walk costs nothing on ordinary strings. */
const MAYBE_KEY_RE = /data-enc/i;

/** The block snapshot's encrypted-media nodes carry their key as `k`. */
const ENC_ALG = 'aes-gcm-256';
const isEncryptedNode = (o) => o.enc === ENC_ALG || o.type === 'doc-image' || o.type === 'doc-video';

/** The HTML with every `data-enc-key` / `data-enc` attribute removed. */
export function stripMediaKeyAttrs(html) {
  const s = String(html ?? '');
  return MAYBE_KEY_RE.test(s) ? s.replace(KEY_ATTR_RE, '') : s;
}

/** True when a value (any depth) still carries a key — for tests and guards. */
export function containsMediaKey(value) {
  return MAYBE_KEY_RE.test(JSON.stringify(value ?? null));
}

/**
 * A copy of `value` with every key removed: attributes inside strings, and `k`
 * on an encrypted media node. Copy-on-write — results spread live store
 * objects (`{...task}`), so mutating in place would strip the key from the
 * user's own board. Symbol-keyed fields (the staged UI records) are not part of
 * the wire result and are not copied.
 */
export function scrubMediaKeysDeep(value) {
  if (typeof value === 'string') return stripMediaKeyAttrs(value);
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) {
    let changed = false;
    const out = value.map((item) => {
      const next = scrubMediaKeysDeep(item);
      if (next !== item) changed = true;
      return next;
    });
    return changed ? out : value;
  }
  const dropK = isEncryptedNode(value) && 'k' in value;
  let changed = dropK;
  const out = {};
  for (const [key, item] of Object.entries(value)) {
    if (dropK && key === 'k') continue;
    const next = scrubMediaKeysDeep(item);
    if (next !== item) changed = true;
    out[key] = next;
  }
  return changed ? out : value;
}

const attrOf = (attrs, name) => {
  const m = new RegExp(`\\s${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i').exec(` ${attrs}`);
  return m ? m[1] ?? m[2] ?? m[3] ?? '' : '';
};

/** "🖼 Image: name" / "▶ Video: name" for one `<img>`/`<video>` tag's attributes. */
export function mediaLabel(tag, attrs) {
  const mime = attrOf(attrs, 'data-mime');
  const isVideo = tag.toLowerCase() === 'video' || /^video\//i.test(mime);
  const name = (attrOf(attrs, 'data-name') || attrOf(attrs, 'alt') || (isVideo ? 'video' : 'image'))
    .replace(/[<>]/g, '')
    .trim();
  return `${isVideo ? '▶ Video' : '🖼 Image'}: ${name || 'file'}`;
}

/**
 * Every image/video tag replaced by its readable label on a line of its own —
 * the markdown/text renderers' input. The label carries no URL: without the
 * key the blob is unreadable, and with a `data:` image it would be the bytes.
 */
export function mediaToLabels(html) {
  return String(html ?? '')
    .replace(/<video\b([^>]*)>[\s\S]*?<\/video>/gi, (_m, attrs) => `<br>${mediaLabel('video', attrs)}<br>`)
    .replace(/<img\b([^>]*)>/gi, (_m, attrs) => `<br>${mediaLabel('img', attrs)}<br>`);
}

/**
 * The reverse, for `document_write` with `format: "html"`: an agent that read
 * html (keys stripped) and writes it back would otherwise drop every image —
 * the sanitizer refuses an uploaded image without its key. Re-attaches the key
 * of an image THIS document already holds, matched by `src` (or the pending
 * upload's ref). Never invents one, never moves one between documents.
 */
export function restoreMediaKeys(nextHtml, currentHtml) {
  const known = new Map();
  for (const m of String(currentHtml ?? '').matchAll(/<img\b([^>]*)>/gi)) {
    const key = attrOf(m[1], 'data-enc-key');
    if (!key) continue;
    const enc = attrOf(m[1], 'data-enc') || ENC_ALG;
    const src = attrOf(m[1], 'src');
    const ref = attrOf(m[1], 'data-pending-ref');
    if (src) known.set(`src:${src}`, { key, enc });
    if (ref) known.set(`ref:${ref}`, { key, enc });
  }
  if (!known.size) return String(nextHtml ?? '');
  return String(nextHtml ?? '').replace(/<img\b([^>]*)>/gi, (whole, attrs) => {
    if (attrOf(attrs, 'data-enc-key')) return whole;
    const src = attrOf(attrs, 'src');
    const ref = attrOf(attrs, 'data-pending-ref');
    const hit = (src && known.get(`src:${src}`)) || (ref && known.get(`ref:${ref}`));
    if (!hit) return whole;
    const rest = attrs.replace(/\/\s*$/, '');
    return `<img${rest} data-enc-key="${hit.key}" data-enc="${hit.enc}">`;
  });
}
